import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'

import { createFigureBatcher, figureShapeKey } from './figureBatcher'
import { fetchStudyFigures, fetchStudyFiguresMeta } from './studyFiguresApi'

import { StudyFiguresMeta, StudyFigureFilter } from '@digital-twins/shared-types'
import { FigureBatcher, FigureEntry, FigureRequestShape } from './figureBatcher'
import { QuickRunFiguresRef } from './studyFiguresApi'

/**
 * The cuts the tab may offer, as the figure service listed them. `failed` is
 * drawn as a visible state on every card's segment control, never as a
 * silently missing group. `refused` (a 400: no launch snapshot, not a
 * canonical study, not completed) means the service cannot serve this study
 * at all: its cards keep today's charts (owner, W6-R f1: old studies work
 * exactly as before).
 */
export type SurveyFiguresMetaState =
  | { status: 'loading' }
  | { status: 'ready'; meta: StudyFiguresMeta }
  | { status: 'failed' }
  | { status: 'refused' }

/** A 400 from the figure service: the study (or the cut) is refused, asking again cannot change it. */
export function isFigureServiceRefusal(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 400
}

/**
 * The report's figure source (plots plan D3). Mounted once per report page
 * (W6-R: summary cards, the survey tab and the data-mode dashboard all draw
 * from it); a QuestionCard rendered outside any source keeps its own path.
 */
export interface SurveyFiguresSource {
  batcher: FigureBatcher
  /** `share` serves question and assigned-asset cuts only (D2). */
  access: 'owner' | 'share'
  meta: SurveyFiguresMetaState
  retryMeta: () => void
  /** `owner:<studyId>`, `share:<token>` or `quick:<runToken>`: a nested provider for the same access reuses this source. */
  accessKey?: string
  /** Asks for the list of cuts when the provider reads it on demand; a no-op once asked. */
  ensureMeta?: () => void
  /**
   * Filters the reader applied on the survey tab (AND across cards). Each card's
   * own filter merges with these before the batched figures request.
   */
  sharedFilters: Array<StudyFigureFilter>
  upsertSharedFilter: (filter: StudyFigureFilter) => void
  removeSharedFilter: (filter: StudyFigureFilter) => void
  /**
   * What a card does when the service cannot draw its default figure. `show`
   * (the survey tab, D3): the visible error or refusal state. `legacy` (the
   * summary and the data-mode report, which drew today's charts before the
   * figure service): today's chart, so nothing that works today breaks. A cut
   * the reader picked always shows its state.
   */
  failurePolicy?: SurveyFiguresFailurePolicy
}

export type SurveyFiguresFailurePolicy = 'show' | 'legacy'

const SurveyFiguresContext = createContext<SurveyFiguresSource | null>(null)

/** The meta route's locale shape (`STUDY_FIGURE_LOCALE_PATTERN` in Nest). */
const META_LOCALE_PATTERN = /^[A-Za-z]{2,3}([-_][A-Za-z0-9]{2,8})*$/

/** The UI language i18next resolved to, or undefined when the route would refuse it. */
function metaLocaleOf(resolvedLanguage: string | undefined): string | undefined {
  return resolvedLanguage && META_LOCALE_PATTERN.test(resolvedLanguage) ? resolvedLanguage : undefined
}

/**
 * `eager` reads the list of cuts on mount (the survey tab). `on-demand` waits
 * until a card first asks for a figure, so a page-level source costs nothing on
 * a tab that draws no question card.
 */
export type SurveyFiguresMetaLoading = 'eager' | 'on-demand'

interface SurveyFiguresProviderProps {
  studyId?: string
  shareToken?: string
  /** A completed Quick Study run, read by its owner (Quick "Explore data"). */
  quickRun?: QuickRunFiguresRef
  metaLoading?: SurveyFiguresMetaLoading
  /** See `SurveyFiguresSource.failurePolicy`; applies to this provider's subtree. */
  failurePolicy?: SurveyFiguresFailurePolicy
  /**
   * The displayed report's revision as the page knows it (the deck's
   * `createdAt`). A change re-reads the list of cuts, whose server revision
   * keys the batcher, so a regenerated report redraws without a reload.
   */
  revisionHint?: string
  children: ReactNode
}

function accessKeyOf(
  studyId: string | undefined,
  shareToken: string | undefined,
  quickRunToken: string | undefined,
): string | undefined {
  if (shareToken) {
    return `share:${shareToken}`
  }
  if (quickRunToken) {
    return `quick:${quickRunToken}`
  }
  return studyId ? `owner:${studyId}` : undefined
}

export function SurveyFiguresProvider(props: SurveyFiguresProviderProps) {
  const { studyId, shareToken, quickRun, metaLoading = 'eager', failurePolicy = 'show', revisionHint, children } = props

  const { i18n } = useTranslation('app')
  const outer = useContext(SurveyFiguresContext)
  const [meta, setMeta] = useState<SurveyFiguresMetaState>({ status: 'loading' })
  const [metaAttempt, setMetaAttempt] = useState(0)
  const [isMetaWanted, setIsMetaWanted] = useState(metaLoading === 'eager')
  const [sharedFilters, setSharedFilters] = useState<Array<StudyFigureFilter>>([])

  const quickThreadToken = quickRun?.threadToken
  const quickDraftToken = quickRun?.draftToken
  const quickRunToken = quickRun?.runToken
  // Keyed by its tokens, so a caller's new object for the same run keeps the source.
  const quickRunRef = useMemo<QuickRunFiguresRef | undefined>(
    () =>
      quickThreadToken && quickDraftToken && quickRunToken
        ? { threadToken: quickThreadToken, draftToken: quickDraftToken, runToken: quickRunToken }
        : undefined,
    [quickThreadToken, quickDraftToken, quickRunToken],
  )
  const accessKey = accessKeyOf(studyId, shareToken, quickRunRef?.runToken)
  useEffect(() => {
    setSharedFilters([])
  }, [accessKey])
  const upsertSharedFilter = useCallback((filter: StudyFigureFilter) => {
    setSharedFilters((current) => {
      const key = `${filter.kind}:${filter.source}`
      return [...current.filter((item) => `${item.kind}:${item.source}` !== key), filter]
    })
  }, [])
  const removeSharedFilter = useCallback((filter: StudyFigureFilter) => {
    const key = `${filter.kind}:${filter.source}`
    setSharedFilters((current) => current.filter((item) => `${item.kind}:${item.source}` !== key))
  }, [])
  // A provider nested under one drawn for the same access (the survey tab under
  // the report page's) reuses it: one list of cuts and one batcher per page.
  const isReusingOuter = outer != null && accessKey != null && outer.accessKey === accessKey

  const locale = i18n.language
  const metaLocale = metaLocaleOf(i18n.resolvedLanguage)
  const access: 'owner' | 'share' = shareToken ? 'share' : 'owner'
  // The report revision the cuts were listed for is the one every figures
  // request sends (one source), so a regenerated report answers 409 instead of
  // mixing; none when the list of cuts could not be read.
  const reportRevision = meta.status === 'ready' ? (meta.meta.reportRevision ?? undefined) : undefined
  // A new batcher (and cache) per study, access, report and UI language: chrome
  // is resolved in the request language, so a language switch asks again.
  // A 409 (the report changed since the cuts were read) re-reads them once.
  const onStaleReport = useCallback(() => setMetaAttempt((attempt) => attempt + 1), [])
  const batcher = useMemo(
    () =>
      createFigureBatcher({
        fetchFigures: (request) => fetchStudyFigures({ studyId, shareToken, quickRun: quickRunRef }, request),
        locale,
        reportRevision,
        onStaleReport,
      }),
    [studyId, shareToken, quickRunRef, locale, reportRevision, onStaleReport],
  )
  useEffect(() => {
    if (isReusingOuter || !isMetaWanted) {
      return
    }
    if (!studyId && !shareToken && !quickRunRef) {
      setMeta({ status: 'failed' })
      return
    }
    const controller = new AbortController()
    // A re-read (new revision hint, a 409) keeps the cuts it has until the new
    // list lands: the batcher is keyed on the revision, so an unchanged
    // revision keeps it (and its cache) and a new one rebuilds it.
    setMeta((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }))
    fetchStudyFiguresMeta({ studyId, shareToken, quickRun: quickRunRef }, metaLocale, controller.signal).then(
      (loaded) => {
        setMeta({ status: 'ready', meta: loaded })
      },
      (error: unknown) => {
        if (!controller.signal.aborted) {
          setMeta(isFigureServiceRefusal(error) ? { status: 'refused' } : { status: 'failed' })
        }
      },
    )
    return () => controller.abort()
  }, [studyId, shareToken, quickRunRef, metaLocale, metaAttempt, isMetaWanted, isReusingOuter, revisionHint])

  const retryMeta = useCallback(() => setMetaAttempt((attempt) => attempt + 1), [])
  const ensureMeta = useCallback(() => setIsMetaWanted(true), [])

  const source = useMemo(
    () => ({
      batcher,
      access,
      meta,
      retryMeta,
      accessKey,
      ensureMeta,
      failurePolicy,
      sharedFilters,
      upsertSharedFilter,
      removeSharedFilter,
    }),
    [
      batcher,
      access,
      meta,
      retryMeta,
      accessKey,
      ensureMeta,
      failurePolicy,
      sharedFilters,
      upsertSharedFilter,
      removeSharedFilter,
    ],
  )
  // Reused, the outer source keeps its list and batcher; the policy is this subtree's.
  const reused = useMemo(
    () => (isReusingOuter && outer ? { ...outer, failurePolicy } : null),
    [isReusingOuter, outer, failurePolicy],
  )

  return <SurveyFiguresContext.Provider value={reused ?? source}>{children}</SurveyFiguresContext.Provider>
}

interface SurveyFiguresSourceProviderProps {
  source: SurveyFiguresSource
  children: ReactNode
}

/** Test and gallery seam: a source built by the caller (no network). */
export function SurveyFiguresSourceProvider(props: SurveyFiguresSourceProviderProps) {
  const { source, children } = props

  return <SurveyFiguresContext.Provider value={source}>{children}</SurveyFiguresContext.Provider>
}

export function useSurveyFigures(): SurveyFiguresSource | null {
  return useContext(SurveyFiguresContext)
}

const IDLE: FigureEntry = { status: 'loading' }

function noopSubscribe(): () => void {
  return () => undefined
}

/**
 * One question's figure under one cut and filter set. `questionId` null (or no
 * source) asks for nothing, and nothing is asked while the list of cuts (the
 * report revision every request sends) is still loading; an on-demand source
 * starts reading it here. The request is
 * batched with every other card that asks in the same tick.
 */
export function useQuestionFigure(
  source: SurveyFiguresSource | null,
  questionId: string | null,
  shape: FigureRequestShape,
): FigureEntry {
  const shapeKey = figureShapeKey(shape)
  const batcher =
    source && source.meta.status !== 'loading' && source.meta.status !== 'refused' ? source.batcher : null

  const readEntry = () => (batcher && questionId ? batcher.get(questionId, shape) : IDLE)
  // The same read on the server (static renders, the survey gallery).
  const entry = useSyncExternalStore(batcher ? batcher.subscribe : noopSubscribe, readEntry, readEntry)
  const ensureMeta = source?.ensureMeta
  useEffect(() => {
    if (ensureMeta && questionId) {
      ensureMeta()
    }
  }, [ensureMeta, questionId])
  useEffect(() => {
    if (batcher && questionId) {
      batcher.request(questionId, shape)
    }
    // `shape` is keyed by its canonical JSON.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batcher, questionId, shapeKey])

  return entry
}
